import { Controller } from "@hotwired/stimulus";
import { smartGuideFrontendSettings } from "../../Shared/webTranslations";

type TurboUrlEvent = CustomEvent<{ url?: string | URL }>;
type TurboRenderEvent = CustomEvent<{ newBody?: HTMLElement }>;
type TurboWindow = Window & {
    Turbo?: {
        cache?: {
            clear(): void;
        };
    };
};

export default class WebIntegrationNavigationGuardController extends Controller<HTMLElement> {
    connect(): void {
        if (!this.isActive()) {
            return;
        }

        document.addEventListener("turbo:before-visit", this.onTurboNavigation as EventListener, true);
        document.addEventListener("turbo:before-fetch-request", this.onTurboNavigation as EventListener, true);
        document.addEventListener("turbo:before-render", this.onTurboBeforeRender as EventListener, true);
        document.addEventListener("submit", this.onSubmit, true);
    }

    disconnect(): void {
        document.removeEventListener("turbo:before-visit", this.onTurboNavigation as EventListener, true);
        document.removeEventListener("turbo:before-fetch-request", this.onTurboNavigation as EventListener, true);
        document.removeEventListener("turbo:before-render", this.onTurboBeforeRender as EventListener, true);
        document.removeEventListener("submit", this.onSubmit, true);
    }

    private readonly onTurboNavigation = (event: TurboUrlEvent): void => {
        this.rejectOutsideNavigation(event, event.detail?.url);
    };

    private readonly onSubmit = (event: SubmitEvent): void => {
        const form = event.target instanceof HTMLFormElement ? event.target : undefined;
        if (!form || (form.target && form.target.toLowerCase() !== "_self")) {
            return;
        }

        this.rejectOutsideNavigation(event, form.action);
    };

    private readonly onTurboBeforeRender = (event: TurboRenderEvent): void => {
        const nextIntegrationId = event.detail?.newBody?.dataset.integrationKey;
        const currentIntegrationId = document.body.dataset.integrationKey;
        if (!nextIntegrationId || !currentIntegrationId || nextIntegrationId === currentIntegrationId) {
            return;
        }

        (window as TurboWindow).Turbo?.cache?.clear();
        document
            .querySelectorAll<HTMLElement>("[data-web-integration-persistent][data-turbo-permanent]")
            .forEach(element => element.removeAttribute("data-turbo-permanent"));
    };

    private rejectOutsideNavigation(event: Event, candidate: string | URL | undefined): void {
        if (this.isInsideIntegration(candidate)) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        console.error("Web integration navigation blocked.", {
            category: "outside-integration",
            deliveryKind: smartGuideFrontendSettings.integrationDeliveryKind
        });
    }

    private isInsideIntegration(candidate: string | URL | undefined): boolean {
        if (!candidate) {
            return false;
        }

        try {
            const url = new URL(candidate.toString(), window.location.href);
            if (url.origin !== window.location.origin) {
                return false;
            }

            const compactBase = this.compactBase();
            return compactBase === ""
                || url.pathname === compactBase
                || url.pathname.startsWith(`${compactBase}/`);
        } catch {
            return false;
        }
    }

    private compactBase(): string {
        const configured = smartGuideFrontendSettings.integrationCompactBase?.trim() ?? "";
        if (configured === "/") {
            return "";
        }

        return configured.replace(/\/+$/, "");
    }

    private isActive(): boolean {
        return Boolean(
            smartGuideFrontendSettings.integrationId
            && smartGuideFrontendSettings.integrationDeliveryKind
            && smartGuideFrontendSettings.integrationDestinationId
            && smartGuideFrontendSettings.integrationCompactBase !== null
            && smartGuideFrontendSettings.integrationCompactBase !== undefined
        );
    }
}
